import { ConfigurationState, ConfigurationScope, DisplayState, SettlementComissionState } from './configuration.models';
import {
  setAllowCashBet,
  setDisplayState,
  setLocalSettings,
  setUSState,
  successCoreSettings,
  successFxRates,
  CoreSettingsResponse
} from './configuration.actions';
import { cloneDeep } from 'lodash-es';
import { Action, createReducer, on } from '@ngrx/store';
import { Config } from "../../services/config/config.service";

// Helper functions for core settings processing
const processSportCategories = (categories: any): any => {
  return categories.map((category: any) => ({
    ...category,
    code: category.code === 'HORSE_RACING' ? 'RACING' : category.code
  }));
};

const processGroupTickets = (
  localSettings: ConfigurationScope.localSettings,
  response: CoreSettingsResponse,
  currentMaxGroupTickets: number | undefined
): number | undefined => {
  const shouldInitGroupTickets = !!(localSettings.haveGroupedTickets && response.max_grouped_tickets);
  return shouldInitGroupTickets ? response.max_group_tickets : currentMaxGroupTickets;
};

const processCommentarySettings = (
  localSettings: ConfigurationScope.localSettings,
  response: CoreSettingsResponse
): {
  isCommentaryActive: boolean;
  settings: { delete: number | null; edit: number | null; } | undefined;
  updatedLocalSettings: ConfigurationScope.localSettings | undefined;
} => {
  const isCommentaryActive = localSettings.isCommentaryActive
    ? !!(response.post_delete_window_in_miliseconds && response.post_edit_window_in_miliseconds)
    : false;

  const settings = isCommentaryActive ? {
    delete: response.post_delete_window_in_miliseconds!,
    edit: response.post_edit_window_in_miliseconds!
  } : undefined;

  const updatedLocalSettings = !isCommentaryActive
    ? { ...localSettings, isCommentaryActive }
    : undefined;

  return { isCommentaryActive, settings, updatedLocalSettings };
};

const processSettlementCommission = (response: CoreSettingsResponse): SettlementComissionState | null => {
  const hasSettlementThresholdCommission = !!(response.settlement_threshold_commission && response.currency_threshold);
  return !hasSettlementThresholdCommission ? null : {
    // hasSettlementThresholdCommission true guarantees both fields are truthy/defined (checked above)
    settlement_threshold_commission: response.settlement_threshold_commission!,
    currency_threshold: response.currency_threshold!
  };
};

export const initialState: ConfigurationState = {
  coreSettings: undefined,
  localSettings: cloneDeep(new Config().CONFIG),
  fxRates: undefined,
  displayState: {
    microsite_type: null,
    view: null,
    sport_category_id: null,
    display_sports: 'display_groups',
    display_racing: 'display_groups',
    display_fantasy: 'display_groups',
    display_results: 'running_pools'
  },
  win_commission: undefined,
  wager_tax: undefined,
  settlement_commission: undefined,
  micrositeCurrency: undefined,
  max_group_tickets: undefined,
  commentarySettings: undefined,
  allow_cash_bet: true,
  usa_state: undefined
};

const reducer = createReducer(
  initialState,
  on(successCoreSettings, (state, action) => {
    const response = action.response;
    
    // Process core settings with sport categories update
    const finalSettings = {
      ...response,
      sport_categories: processSportCategories(response.sport_categories)
    };

    // Process group tickets
    const maxGroupTickets = processGroupTickets(
      state.localSettings!,
      response,
      state.max_group_tickets
    );

    // Process commentary settings
    const { settings: commentarySettings, updatedLocalSettings } = processCommentarySettings(
      state.localSettings!,
      response
    );

    // Process settlement commission
    const settlementCommission = processSettlementCommission(response);

    return {
      ...state,
      coreSettings: finalSettings,
      max_group_tickets: maxGroupTickets,
      localSettings: updatedLocalSettings || state.localSettings,
      commentarySettings: commentarySettings || state.commentarySettings,
      win_commission: response.win_commission,
      wager_tax: response.reversed_wager_tax,
      micrositeCurrency: response.default_currency,
      settlement_commission: settlementCommission,
      isLoaded: true
    };
  }),

  on(successFxRates, (state, action) => ({
    ...state,
    fxRates: action.response as any // FxRate[] from API, stored as single FxRates in state (legacy structure)
  })),

  on(setDisplayState, (state, { displayState }) => {
    const sportCategory = state.coreSettings?.sport_categories?.find(
      category => category.code === displayState.view
    );

    const newDisplayState: DisplayState = {
      ...state.displayState,
      ...displayState,
      sport_category_id: sportCategory?.id?.toString() ?? null
    };

    return {
      ...state,
      displayState: newDisplayState
    };
  }),

  on(setLocalSettings, (state, action) => {
    // Only update if there are actual changes
    const hasChanges = (Object.keys(action.localSettings) as (keyof ConfigurationScope.localSettings)[]).some(
      key => action.localSettings[key] !== state.localSettings?.[key]
    );

    if (!hasChanges) {
      return state;
    }

    return {
      ...state,
      localSettings: {
        ...state.localSettings,
        ...action.localSettings
      }
    };
  }),

  on(setUSState, (state, action) => ({
    ...state,
    usa_state: action.usa_state
  })),

  on(setAllowCashBet, (state, action) => ({
    ...state,
    allow_cash_bet: action.allow_cash_bet
  }))
);

export const ws = {
  RELOAD_EVENTS: [
    'MICROSITE_AUTHENTICATION_REFRESH',
    'USER_REGISTER',
    'USER_LOGIN',
    'USER_LOGOUT'
  ]
};

export function configurationReducer(
  state: ConfigurationState | undefined,
  action: Action
): ConfigurationState {
  return reducer(state, action);
}
